/**
 * x-sidebar-nav directive.
 *
 * Manages a sidebar navigation component with two sub-directives. The root
 * reads links from the `[x-sidebar-nav:items]` container and exposes them as
 * reactive data so they can be duplicated in a secondary navigation (e.g. a
 * sticky header). The `:header` sub-directive animates the secondary header
 * out of view as the user scrolls down the page, using GSAP ScrollTrigger.
 *
 * Sub-directives:
 *   x-sidebar-nav:items  - Applied to the primary link container. Registers
 *                          a `$refs.items` reference.
 *   x-sidebar-nav:header - Applied to the secondary sticky header. Creates a
 *                          GSAP ScrollTrigger that slides the element off screen
 *                          once 80% of the viewport has been scrolled past.
 *
 * @options (root only)
 *   matchMediaQuery - A CSS media query string. When provided, the `:header`
 *                     scroll tween is only created when the query matches.
 *
 * @example
 *   <div x-sidebar-nav="{ matchMediaQuery: '(min-width: 1024px)' }">
 *     <nav x-sidebar-nav:items>
 *       <a href="#section-1">Section 1</a>
 *     </nav>
 *     <div x-sidebar-nav:header>…sticky header…</div>
 *   </div>
 */
(() => {
  const root = (Alpine, el, { matchMediaQuery }) => {
    Alpine.bind(el, () => ({
      ["x-data"]() {
        return {
          links: [],
          matchMediaQuery,

          /**
           * Reads links from the items container on mount.
           */
          init() {
            this.syncLinks();
          },

          /**
           * Queries all anchor elements inside the `[x-sidebar-nav:items]`
           * container and copies their attributes and inner HTML into the
           * reactive `links` array. `class` and `id` attributes are excluded
           * to allow independent styling in secondary contexts.
           */
          syncLinks() {
            const links = this.$el.querySelectorAll(
              "[x-sidebar-nav\\:items] a",
            );

            Array.from(links).forEach((node) => {
              const attributes = {};

              Array.from(node.attributes).forEach((attribute) => {
                attributes[attribute.name] = attribute.value;
              });

              delete attributes.class;
              delete attributes.id;

              this.links.push({
                innerHTML: node.innerHTML,
                ...attributes,
              });
            });
          },
        };
      },
    }));
  };

  /**
   * Marks the items container with `x-ref="items"`.
   *
   * @param {object} Alpine - The Alpine.js instance.
   * @param {HTMLElement} el - The items container element.
   */
  const items = (Alpine, el) => {
    Alpine.bind(el, () => ({
      ["x-ref"]: "items",
    }));
  };

  /**
   * Animates the sticky header element off screen as the user scrolls.
   * Watches `isVisible` to update the `--spacing-main-top-offset` CSS custom
   * property so the page content adjusts accordingly.
   *
   * @param {object} Alpine - The Alpine.js instance.
   * @param {HTMLElement} el - The sticky header element.
   */
  const header = (Alpine, el) => {
    Alpine.bind(el, () => ({
      ["x-init"]() {
        /**
         * Creates a GSAP tween that slides the element downward (off screen) as
         * the user scrolls past 80% of the viewport height.
         */
        const createTween = () => {
          let offset = window.innerHeight * 0.8;

          if (
            window.siteMain &&
            window.siteMain.clientHeight < window.innerHeight
          ) {
            offset = 0;
          }

          this.$gsap.fromTo(
            this.$el,
            {
              y: 0,
            },
            {
              y: "100%",
              duration: 0.25,
              scrollTrigger: {
                start: () => `${offset} top`,
                end: "bottom bottom",
                toggleActions: "play reverse play reverse",
              },
              onComplete: () => (this.hidden = false),
              onReverseComplete: () => (this.hidden = true),
            },
          );
        };

        if (this.matchMediaQuery) {
          this.$gsap.matchMedia().add(this.matchMediaQuery, () => {
            createTween();
          });
        } else {
          createTween();
        }

        this.$watch("isVisible", (value) => {
          if (value) {
            const headerHeight = window["siteHeader"].clientHeight;
            const height = this.$el.clientHeight;

            this.$app.$setProperty(
              "--spacing-main-top-offset",
              this.$app.$toRem(headerHeight + height),
            );
          } else {
            this.$app.$setProperty("--spacing-main-top-offset", null);
          }
        });
      },

      ["x-data"]() {
        return {
          hidden: true,

          /**
           * Returns true when the sticky header is visible (scrolled into
           * its fully-shown state).
           * @returns {boolean}
           */
          get isVisible() {
            return !this.hidden;
          },
        };
      },
    }));
  };

  document.addEventListener("alpine:init", () => {
    Alpine.directive(
      "sidebar-nav",
      (el, { expression, value }, { evaluate }) => {
        const options = expression ? evaluate(expression) : {};

        if ("items" === value) {
          items(Alpine, el, options);
        } else if ("header" === value) {
          header(Alpine, el, options);
        } else {
          root(Alpine, el, options);
        }
      },
    );
  });
})();
